import { apiAmountToSom, formatSomNumber } from "./apiMoney";

/**
 * Дата списания: с вложенного `subscription_id` или с полей точки/планшета.
 * @param {Record<string, unknown>} row
 * @returns {unknown}
 */
function getNextBillingFromRow(row) {
  const sub = row.subscription_id;
  if (sub && typeof sub === "object" && sub !== null) {
    const s = /** @type {Record<string, unknown>} */(sub);
    if ("next_billing_date" in s) {
      const v = s.next_billing_date;
      if (v != null && v !== "") {
        return v;
      }
    }
  }
  return (
    row.next_billing_date ?? row.nextPaymentDate ?? row.next_payment_date
  );
}

/**
 * @param {unknown} d
 * @returns {string}
 */
function formatDateRuOrDash(d) {
  if (d == null) {
    return "—";
  }
  const dt = new Date(/** @type {string|number|Date} */(d));
  if (isNaN(dt.getTime())) {
    return "—";
  }
  return dt.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Цена/сумма из API в минимальных единицах (×100 к сумам) → строка для UI.
 * @param {unknown} rawMinor
 * @returns {string | null}
 */
export function formatTiyinToSom(rawMinor) {
  if (rawMinor == null || rawMinor === "") {
    return null;
  }
  const n = Number(rawMinor);
  if (Number.isNaN(n) || n === 0) {
    return null;
  }
  const soms = apiAmountToSom(rawMinor);
  if (soms == null || soms === 0) {
    return null;
  }
  return `${formatSomNumber(soms)} сум`;
}

/**
 * Ненулевые цены плана: «X сум / в месяц» и т.д.
 * @param {Record<string, unknown> | null | undefined} plan
 * @returns {{ key: string, line: string }[]}
 */
export function getPlanPriceLines(plan) {
  if (!plan || typeof plan !== "object") {
    return [];
  }
  const items = [
    { key: "m", field: "price_per_month", label: "в месяц" },
    { key: "q", field: "price_per_quarter", label: "за квартал" },
    { key: "h", field: "price_per_half_year", label: "за полгода" },
    { key: "y", field: "price_per_year", label: "в год" },
  ];
  const out = [];
  for (const { key, field, label } of items) {
    const fmt = formatTiyinToSom(plan[field]);
    if (fmt) {
      out.push({ key, line: `${fmt} / ${label}` });
    }
  }
  return out;
}

/**
 * @param {Record<string, unknown>} row
 * @returns {{ _id: unknown, planId: unknown, startDate: unknown, endDate: unknown, nextBilling: unknown, plan: Record<string, unknown> | null } | null}
 */
export function getSubscriptionInfo(row) {
  const sub = row.subscription_id;
  if (!sub || typeof sub !== "object" || sub === null || Array.isArray(sub)) {
    return null;
  }
  const s = /** @type {Record<string, unknown>} */(sub);
  const planRaw = s.plan;
  const plan =
    planRaw &&
    typeof planRaw === "object" &&
    planRaw !== null &&
    !Array.isArray(planRaw)
      ? /** @type {Record<string, unknown>} */(planRaw)
      : null;
  return {
    _id: s._id,
    planId: s.plan_id,
    startDate: s.start_date,
    endDate: s.end_date,
    nextBilling: s.next_billing_date,
    plan,
  };
}

/**
 * @param {unknown} s
 * @returns {"active" | "inactive" | "other"}
 */
function normalizeStatus(s) {
  if (s == null) return "other";
  const t = String(s).toLowerCase().trim();
  if (["inactive", "disabled", "off", "not_active", "in_active"].includes(t)) {
    return "inactive";
  }
  if (["active", "enabled", "on"].includes(t)) {
    return "active";
  }
  return "other";
}

/**
 * Покрасить карточку / показать предупреждение, если `next_billing_date` — сегодня
 * или уже в прошлом (текущая дата >= даты списания по календарю).
 * @param {Record<string, unknown>} row
 * @returns {boolean}
 */
export function isNextBillingDueOrOverdue(row) {
  const d = getNextBillingFromRow(row);
  if (d == null) {
    return false;
  }
  const next = new Date(/** @type {string|number|Date} */(d));
  if (isNaN(next.getTime())) {
    return false;
  }
  const now = new Date();
  const startToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const startNext = new Date(
    next.getFullYear(),
    next.getMonth(),
    next.getDate(),
  );
  const diffDays = (startNext - startToday) / 864e5;
  return diffDays <= 0;
}

/**
 * Предупреждение о ежемесячном платеже, только если `next_billing_date` сегодня
 * или раньше (тот же порог, что и красная рамка).
 * @param {Record<string, unknown>} row
 * @returns {string | null}
 */
export function getMonthlyPayWarningText(row) {
  const d = getNextBillingFromRow(row);
  if (d == null) {
    return null;
  }
  if (!isNextBillingDueOrOverdue(row)) {
    return null;
  }
  const next = new Date(/** @type {string|number|Date} */(d));
  if (isNaN(next.getTime())) {
    return null;
  }
  const now = new Date();
  const startToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );

  const startNext = new Date(
    next.getFullYear(),
    next.getMonth(),
    next.getDate(),
  );

  const diffDays = (startNext - startToday) / 864e5;
  if (diffDays < 0) {
    return "Просрочен ежемесячный платёж. Пополните баланс или оформите оплату.";
  }
  return "Сегодня дата ежемесячного списания. Убедитесь, что на счёте хватит средств.";
}

/**
 * BEM-модификатор для <li className="tabletRow ...">.
 * Красный только по просрочке/сегодня {@link isNextBillingDueOrOverdue};
 * срочное окончание подписки по `end_date` к цвету строки не привязываем — иначе при доминировании
 * этого условия зелёный (active) почти не появляется.
 * @param {Record<string, unknown>} row
 * @returns {string}
 */
export function getTabletRowModifierClass(row) {
  if (isNextBillingDueOrOverdue(row)) {
    return "tabletRow--paymentUrgent";
  }
  const st = normalizeStatus(row.status);
  if (st === "inactive") {
    return "tabletRow--inactive";
  }
  if (st === "active") {
    return "tabletRow--active";
  }
  return "tabletRow--neutral";
}

/**
 * @param {Record<string, unknown>} row
 * @returns {string}
 */
export function formatNextPaymentDate(row) {
  return formatDateRuOrDash(getNextBillingFromRow(row));
}

/**
 * @param {unknown} d
 * @returns {string}
 */
export function formatSubscriptionDate(d) {
  return formatDateRuOrDash(d);
}

/**
 * @param {unknown} status
 * @returns {string}
 */
export function formatStatusLabel(status) {
  if (status == null || status === "") {
    return "—";
  }
  const t = String(status);
  const low = t.toLowerCase();
  if (low === "active") return "Активен";
  if (low === "inactive") return "Неактивен";
  return t;
}
